import {
  catalogVolumeToBodymap,
  DAY_MS,
  detectRegression,
  e1rmPointsByTrack,
  normalizeVolumeByMuscle,
  repsAtLoadBySlug,
  volumeByPattern,
  type E1rmPoint,
  type Exercise,
  type LogSet,
  type MovementPattern,
  type MuscleSlug,
  type PainSeverity,
  type RegressionResult,
  type RepsAtLoadPoint,
  type TimedSessionResult,
} from "@jackedlogs/shared";

// Pure aggregation behind the Tier-1 progress panels (lifted from mobile
// with the panels). The panels keep the data hook, i18n, theme and
// render; the windowing / ranking / regression math lives here so it can
// be unit-tested.

// =========================================================================
// MovementBalancePanel — sets per movement pattern, headline window + 7d inset
// =========================================================================
// PG-1 period selector: `windowDays` drives the headline (was fixed 28d); the
// small "X/7d" inset stays a fixed 7-day recent-push reference regardless of
// the selected window. Default 28 keeps the pre-selector behaviour identical.
export type MovementBalanceRow = {
  pattern: MovementPattern;
  setsWindow: number;
  sets7d: number;
  volumeWindowKg: number;
};

export function computeMovementBalance(
  results: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  nowMs: number,
  windowDays = 28,
): MovementBalanceRow[] {
  const byWindow = volumeByPattern(results, catalog, nowMs, windowDays);
  const by7d = volumeByPattern(results, catalog, nowMs, 7);
  return (Object.keys(byWindow) as MovementPattern[])
    .map((p) => ({
      pattern: p,
      setsWindow: byWindow[p].sets,
      sets7d: by7d[p].sets,
      volumeWindowKg: byWindow[p].volumeKg,
    }))
    .filter((r) => r.setsWindow > 0)
    .sort((a, b) => b.setsWindow - a.setsWindow);
}

// =========================================================================
// PainVolumePanel — per-muscle pain × recent volume snapshot
// =========================================================================
// PG-1 period selector: `windowDays` drives the headline volume (was 28d); the
// "X / 7d" inset stays a fixed 7-day recent reference. Default 28 keeps the
// pre-selector behaviour identical.
export type PainVolumeRow = {
  muscleSlug: MuscleSlug;
  severity: PainSeverity;
  volume7dKg: number;
  volumeWindowKg: number;
};

export function computePainVolume(
  results: TimedSessionResult[],
  painMap: Partial<Record<MuscleSlug, PainSeverity>>,
  nowMs: number,
  windowDays = 28,
): PainVolumeRow[] {
  const muscles = Object.entries(painMap) as Array<[MuscleSlug, PainSeverity]>;
  if (muscles.length === 0) return [];
  const cutoff7d = nowMs - 7 * DAY_MS;
  const cutoffWindow = nowMs - windowDays * DAY_MS;
  const out: PainVolumeRow[] = [];
  for (const [slug, sev] of muscles) {
    let v7 = 0;
    let vWindow = 0;
    for (const ts of results) {
      const ms = ts.startedAtMs;
      if (ms < cutoffWindow || ms > nowMs) continue;
      // volumeByMuscle is catalog vocab. The pain picker works in bodymap
      // vocab (anatomical regions), so bridge the catalog map back through
      // catalogVolumeToBodymap before the bodymap-slug lookup.
      // normalizeVolumeByMuscle handles legacy bodymap-keyed rows.
      const bodymapVolume = catalogVolumeToBodymap(
        normalizeVolumeByMuscle(ts.result.volumeByMuscle),
      );
      const m = bodymapVolume[slug] ?? 0;
      if (m <= 0) continue;
      if (ms >= cutoff7d) v7 += m;
      vWindow += m;
    }
    out.push({
      muscleSlug: slug,
      severity: sev,
      volume7dKg: v7,
      volumeWindowKg: vWindow,
    });
  }
  return out.sort((a, b) => b.volume7dKg - a.volume7dKg);
}

// =========================================================================
// E1RMTrendsPanel — top lift per pattern + sparkline + regression flag
// =========================================================================
// Isolation / cardio / other are excluded because a regression alert on a
// bicep curl or a treadmill bout isn't actionable like one on the squat.
// The order here is the rendering order.
export const TREND_PATTERNS: MovementPattern[] = [
  "squat",
  "hinge",
  "horizontal-push",
  "vertical-push",
  "horizontal-pull",
  "vertical-pull",
  "lunge",
  "carry",
  "core",
];

/**
 * One of the champion exercise's OTHER machines (EQ-1-HIST slice 2). Carried
 * so the row can put them behind a tap instead of on screen: Antenore's call
 * was one row per pattern, principal machine shown, the rest reachable.
 *
 * ⛔ Deliberately thinner than the champion — a name and a number, no series
 * and no trend. A second sparkline behind a disclosure would be a second
 * reading competing with the one the row is making; if it turns out he wants
 * the shape too, that is a slice of its own, not a field added on spec.
 */
export type E1RMTrendAlternate = {
  equipmentInstance: string;
  latest: number;
  pointCount: number;
};

export type E1RMTrendEntry = {
  pattern: MovementPattern;
  slug: string;
  snapshot: string | undefined;
  /**
   * Which machine produced the numbers on this row, when the athlete named
   * one. Absent = the Default machine, which is every lift logged before
   * EQ-1-HIST and all of production today.
   */
  equipmentInstance?: string;
  /**
   * The same exercise on the athlete's other machines, best first. Empty for
   * every untagged exercise, so the row renders exactly as it always did.
   */
  alternates: E1RMTrendAlternate[];
  sorted: E1rmPoint[];
  latest: number;
  reg: RegressionResult;
  /**
   * How many of this champion's points are a floor worth trusting —
   * `proven` or `probable` (measurement-contract §3). When 0, points exist but
   * none went near the limit: the trend arrow would be a confident line drawn
   * through sets we know nothing about, so the panel shows the honest
   * "not measurable yet" state instead. ⛔ Counted by explicit predicate, never
   * by ordering the confidence verdicts (E1RM_CONFIDENCES is not a scale).
   */
  trustedCount: number;
};

// PG-1 period selector: `windowDays` bounds both the sparkline aggregation and
// the regression fit (windowWeeks = windowDays/7). Default 84d (= 12 weeks)
// keeps the pre-selector behaviour identical.
export function rankE1RMTrends(
  results: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  nowMs: number,
  windowDays = 84,
): E1RMTrendEntry[] {
  const cutoffWindowMs = nowMs - windowDays * DAY_MS;
  const slugToPattern = new Map<string, MovementPattern>();
  for (const ex of catalog) {
    slugToPattern.set(ex.slug, ex.movementPattern ?? "other");
  }

  // Aggregate e1RM points per exercise within the 12-week window. Name is
  // resolved at render time via the live catalog (FK = exerciseSlug); the
  // snapshot is kept only as a deleted-row fallback.
  type Track = {
    /** The exercise — resolved from the series, never from the map key. */
    slug: string;
    /** The machine, when named. Absent = the Default. */
    instance: string | undefined;
    pattern: MovementPattern;
    snapshot: string | undefined;
    points: E1rmPoint[];
    latest: number;
  };
  // slug → every machine that exercise was lifted on in the window.
  const byExercise = new Map<string, Track[]>();
  // The series come from the shared funnel (e1rmPoints.ts). The pattern is the
  // only thing this panel adds, and it is a catalog lookup — not part of what
  // makes an observation an observation.
  //
  // ⛔ The map key is a TRACK key (exercise + machine, EQ-1-HIST), NOT a slug:
  // for a tagged lift it is an opaque composite that no catalog contains. The
  // exercise rides on `series.slug`, and that is what the catalog lookup and
  // the entry below must both use — keying either off the map key makes the
  // pattern resolve to "other", which TREND_PATTERNS drops, so a lift on a
  // named machine would silently stop being able to win its pattern. Silently:
  // no error, no log, and no test would see it while nobody tags.
  for (const series of e1rmPointsByTrack(results, {
    fromMs: cutoffWindowMs,
    toMs: nowMs,
  }).values()) {
    const bucket = byExercise.get(series.slug);
    const track: Track = {
      slug: series.slug,
      instance: series.equipmentInstance,
      pattern: slugToPattern.get(series.slug) ?? "other",
      snapshot: series.snapshot,
      points: series.points,
      // Already chronological — the funnel sorts once so no consumer has to.
      latest: series.points[series.points.length - 1]?.kgPerRep1 ?? 0,
    };
    if (bucket) bucket.push(track);
    else byExercise.set(series.slug, [track]);
  }

  type Champ = {
    slug: string;
    snapshot: string | undefined;
    instance: string | undefined;
    alternates: E1RMTrendAlternate[];
    sorted: E1rmPoint[];
    latest: number;
  };

  // ⭐ Which machine SPEAKS for the exercise (EQ-1-HIST slice 2). The one that
  // produced the exercise's top reading, because that is the number the panel
  // promises in its own help text — "the exercise where your top e1RM is
  // highest". Picking the busiest machine instead would put a number on the
  // row that is NOT the one that won the pattern, and the row would quietly
  // contradict the legend above it.
  //
  // ⇒ Volume of evidence is the TIE-BREAK, not the rule: same top reading, the
  // machine you have actually used more wins, and failing that the more recent
  // one. Deterministic in every case, so the row cannot flicker between two
  // machines across renders.
  //
  // 📐 Mine, not his (2026-08-19). His words were "the machine with more data
  // (or the most recent)"; the coherence problem above only shows up in code,
  // so this reading is my call and he should overrule it if he meant it
  // literally.
  const speaksForExercise = (tracks: Track[]): Track =>
    tracks.reduce((best, t) => {
      if (t.latest !== best.latest) return t.latest > best.latest ? t : best;
      if (t.points.length !== best.points.length) {
        return t.points.length > best.points.length ? t : best;
      }
      const tAt = t.points[t.points.length - 1]?.atMs ?? 0;
      const bAt = best.points[best.points.length - 1]?.atMs ?? 0;
      return tAt > bAt ? t : best;
    });

  const byPattern = new Map<MovementPattern, Champ>();
  for (const tracks of byExercise.values()) {
    const principal = speaksForExercise(tracks);
    if (!TREND_PATTERNS.includes(principal.pattern)) continue;
    const existing = byPattern.get(principal.pattern);
    if (existing && existing.latest >= principal.latest) continue;
    byPattern.set(principal.pattern, {
      slug: principal.slug,
      snapshot: principal.snapshot,
      instance: principal.instance,
      // Everything else the same exercise was lifted on, heaviest first. The
      // Default machine has no name to show, so it cannot appear here — it is
      // either the principal or it is not in the picture.
      alternates: tracks
        .filter((t) => t !== principal && t.instance !== undefined)
        .sort((a, b) => b.latest - a.latest)
        .map((t) => ({
          equipmentInstance: t.instance as string,
          latest: t.latest,
          pointCount: t.points.length,
        })),
      sorted: principal.points,
      latest: principal.latest,
    });
  }

  return TREND_PATTERNS.flatMap((p) => {
    const champ = byPattern.get(p);
    if (!champ) return [];
    const reg = detectRegression(champ.sorted, nowMs, windowDays / 7);
    // A floor worth trusting = the point went near/to the limit. Explicit
    // predicate, not a comparison — the confidence union is not ordered.
    const trustedCount = champ.sorted.filter(
      (pt) => pt.confidence === "proven" || pt.confidence === "probable",
    ).length;
    return [
      {
        pattern: p,
        slug: champ.slug,
        snapshot: champ.snapshot,
        ...(champ.instance ? { equipmentInstance: champ.instance } : {}),
        alternates: champ.alternates,
        sorted: champ.sorted,
        latest: champ.latest,
        reg,
        trustedCount,
      },
    ];
  });
}

// =========================================================================
// RepsAtLoadTrendsPanel — the SECOND strength axis (measurement-contract §8)
// =========================================================================
// The e1RM axis above ranks ONE lift per movement pattern and excludes
// isolation/machine work (a regression alert on a curl isn't actionable). This
// axis is exactly its complement: it lists exercises DIRECTLY (no pattern
// grouping, no exclusions) and lives for the high-rep, machine, feel-based work
// that never grows an e1RM series. "At the load you keep returning to, are the
// reps climbing at equal-or-better effort?" — a directly observed gain.

// Progress is being slimmed (measurement-contract §1); cap the row count so
// this never becomes a wall. Measured rows win the slots; the honest
// "insufficient" nudges fill whatever remains.
export const REPS_AT_LOAD_MAX_ROWS = 6;

export type RepsAtLoadEntry = {
  slug: string;
  snapshot: string | undefined;
  loadKg: number;
  /** Effort-tagged points at the fixed load, chronological — the comparable
   *  series the trend is fit on (reps at an unknown effort aren't comparable). */
  sorted: RepsAtLoadPoint[];
  /** Reps of the most recent headline set — the number the row leads with. */
  latestReps: number;
  latestAtMs: number;
  reg: RegressionResult;
  /**
   * §3 "measured" vs "insufficient": ≥2 effort-tagged days at the fixed load,
   * so a "then→now" comparison at equal-or-better effort actually exists. When
   * false the load recurs but its sets aren't effort-tagged — the row shows the
   * honest sentence naming the gesture, not a trend.
   */
  measured: boolean;
};

export function rankRepsAtLoad(
  sets: readonly LogSet[],
  nowMs: number,
  windowDays = 90,
): RepsAtLoadEntry[] {
  const cutoffMs = nowMs - windowDays * DAY_MS;
  const entries: RepsAtLoadEntry[] = [];
  for (const [slug, series] of repsAtLoadBySlug(sets, {
    fromMs: cutoffMs,
    toMs: nowMs,
  })) {
    const effortPts = series.points.filter((p) => p.rir != null);
    const measured = series.comparableDays >= 2;
    // Trend fit on the effort-tagged points only. `kgPerRep1` is
    // detectRegression's generic y-slot — here it carries reps. `rir` feeds the
    // same effort-aware downgrade the e1RM axis uses (a reps drop taken easy
    // isn't proven weakness), reused verbatim rather than re-derived.
    const reg = detectRegression(
      effortPts.map((p) => ({ atMs: p.atMs, kgPerRep1: p.reps, rir: p.rir })),
      nowMs,
      windowDays / 7,
    );
    // Effort-tagged points headline a measured row; an insufficient row falls
    // back to its raw points so it can still say "you keep training this load".
    const headline = effortPts.length > 0 ? effortPts : series.points;
    const last = headline[headline.length - 1]!;
    entries.push({
      slug,
      snapshot: series.snapshot,
      loadKg: series.loadKg,
      sorted: effortPts,
      latestReps: last.reps,
      latestAtMs: last.atMs,
      reg,
      measured,
    });
  }
  // Measured first (richer comparison, then more recent); insufficient nudges
  // take the leftover slots, most recent first.
  entries.sort((a, b) => {
    if (a.measured !== b.measured) return a.measured ? -1 : 1;
    if (a.measured && b.sorted.length !== a.sorted.length) {
      return b.sorted.length - a.sorted.length;
    }
    return b.latestAtMs - a.latestAtMs;
  });
  return entries.slice(0, REPS_AT_LOAD_MAX_ROWS);
}
